import {
  CalendarDays,
  Check,
  Clock3,
  Download,
  FileCheck2,
  RotateCcw,
  Search,
  UserX,
  Users,
  X,
} from "lucide-react";
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
} from "react";
import {
  Avatar,
  Button,
  EmptyState,
  PageHeader,
  StatusBadge,
} from "../components/ui";
import {
  downloadTextFile,
  formatDayDate,
  formatTime,
  statusLabel,
  todayISO,
} from "../lib/utils";
import { useApp } from "../state/AppContext";
import type {
  AttendanceRecord,
  AttendanceStatus,
  Student,
} from "../types";
import "../styles/attendance.css";

type AttendanceFilter = AttendanceStatus | "all" | "unrecorded";

interface AttendanceRow {
  student: Student;
  record?: AttendanceRecord;
  status: AttendanceStatus | "unrecorded";
}

const filterLabels: Record<AttendanceFilter, string> = {
  all: "Semua status",
  present: "Hadir",
  late: "Lewat",
  absent: "Tidak hadir",
  excused: "Berizin",
  unrecorded: "Belum direkod",
};

const methodLabels: Record<AttendanceRecord["method"], string> = {
  qr: "Imbas QR",
  manual: "Manual",
  admin: "Pelarasan admin",
};

const csvCell = (value: string | number) =>
  `"${String(value).replaceAll('"', '""')}"`;

export function AttendancePage() {
  const {
    session,
    students,
    attendance,
    classes,
    settings,
    updateAttendance,
    markAttendance,
  } = useApp();
  const today = todayISO();
  const initialClassId =
    session?.role === "teacher" && session.assignedClassId
      ? session.assignedClassId
      : "all";

  const [selectedDate, setSelectedDate] = useState(today);
  const [selectedClassId, setSelectedClassId] = useState(initialClassId);
  const [statusFilter, setStatusFilter] =
    useState<AttendanceFilter>("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [feedback, setFeedback] = useState("");
  const feedbackTimerRef = useRef<number | null>(null);

  const teacherIsRestricted =
    session?.role === "teacher" &&
    !settings.permissions.teacherCanViewOtherClasses;
  const canChooseClass = !teacherIsRestricted;
  const canEditAttendance =
    session?.role === "admin" ||
    (session?.role === "teacher" &&
      settings.permissions.teacherCanEditAttendance);
  const canExport = session?.role === "admin";

  const announce = (message: string) => {
    setFeedback(message);
    if (feedbackTimerRef.current !== null) {
      window.clearTimeout(feedbackTimerRef.current);
    }
    feedbackTimerRef.current = window.setTimeout(() => setFeedback(""), 3500);
  };

  useEffect(
    () => () => {
      if (feedbackTimerRef.current !== null) {
        window.clearTimeout(feedbackTimerRef.current);
      }
    },
    [],
  );

  useEffect(() => {
    setSelectedClassId(
      session?.role === "teacher" && session.assignedClassId
        ? session.assignedClassId
        : "all",
    );
  }, [session?.assignedClassId, session?.id, session?.role]);

  useEffect(() => {
    if (
      teacherIsRestricted &&
      session?.assignedClassId &&
      selectedClassId !== session.assignedClassId
    ) {
      setSelectedClassId(session.assignedClassId);
    }
  }, [selectedClassId, session?.assignedClassId, teacherIsRestricted]);

  const availableClasses = useMemo(() => {
    if (!teacherIsRestricted) return classes;
    return classes.filter((schoolClass) => schoolClass.id === session?.assignedClassId);
  }, [classes, session?.assignedClassId, teacherIsRestricted]);

  const authorisedStudents = useMemo(() => {
    if (!teacherIsRestricted || !session?.assignedClassId) return students;
    return students.filter((student) => student.classId === session.assignedClassId);
  }, [session?.assignedClassId, students, teacherIsRestricted]);

  const classStudents = useMemo(
    () =>
      selectedClassId === "all"
        ? authorisedStudents
        : authorisedStudents.filter(
            (student) => student.classId === selectedClassId,
          ),
    [authorisedStudents, selectedClassId],
  );

  const recordsForDate = useMemo(
    () => attendance.filter((record) => record.date === selectedDate),
    [attendance, selectedDate],
  );

  const recordsByStudent = useMemo(() => {
    const map = new Map<string, AttendanceRecord>();
    recordsForDate.forEach((record) => {
      if (!map.has(record.studentId)) map.set(record.studentId, record);
    });
    return map;
  }, [recordsForDate]);

  const allRows = useMemo<AttendanceRow[]>(
    () =>
      classStudents
        .map((student): AttendanceRow => {
          const record = recordsByStudent.get(student.id);
          return {
            student,
            record,
            status: record?.status ?? "unrecorded",
          };
        })
        .sort(
          (left, right) =>
            left.student.className.localeCompare(right.student.className, "ms") ||
            left.student.name.localeCompare(right.student.name, "ms"),
        ),
    [classStudents, recordsByStudent],
  );

  const visibleRows = useMemo(() => {
    const query = searchQuery.trim().toLocaleLowerCase("ms");
    return allRows.filter((row) => {
      const matchesStatus =
        statusFilter === "all" || row.status === statusFilter;
      const matchesSearch =
        !query ||
        row.student.name.toLocaleLowerCase("ms").includes(query) ||
        row.student.code.toLocaleLowerCase("ms").includes(query);
      return matchesStatus && matchesSearch;
    });
  }, [allRows, searchQuery, statusFilter]);

  const totals = useMemo(
    () =>
      allRows.reduce(
        (result, row) => {
          result[row.status] += 1;
          return result;
        },
        {
          present: 0,
          late: 0,
          absent: 0,
          excused: 0,
          unrecorded: 0,
        },
      ),
    [allRows],
  );

  const defaultClassId =
    session?.role === "teacher" && session.assignedClassId
      ? session.assignedClassId
      : "all";
  const hasActiveFilters =
    selectedDate !== today ||
    selectedClassId !== defaultClassId ||
    statusFilter !== "all" ||
    Boolean(searchQuery.trim());
  const selectedClassName =
    selectedClassId === "all"
      ? "Semua kelas"
      : classes.find((schoolClass) => schoolClass.id === selectedClassId)?.name ??
        "Kelas";

  const resetFilters = () => {
    setSelectedDate(today);
    setSelectedClassId(defaultClassId);
    setStatusFilter("all");
    setSearchQuery("");
  };

  const exportCsv = () => {
    const rows = [
      [
        "Tarikh",
        "Kod Murid",
        "Nama Murid",
        "Kelas",
        "Status",
        "Masa",
        "Kaedah",
        "Direkod Oleh",
      ],
      ...visibleRows.map((row) => [
        selectedDate,
        row.student.code,
        row.student.name,
        row.student.className,
        row.status === "unrecorded"
          ? filterLabels.unrecorded
          : statusLabel[row.status],
        row.record ? formatTime(row.record.timestamp) : "",
        row.record ? methodLabels[row.record.method] : "",
        row.record?.recordedBy ?? "",
      ]),
    ];
    const csv = rows
      .map((row) => row.map((value) => csvCell(value)).join(","))
      .join("\n");
    const classSlug =
      selectedClassId === "all"
        ? "semua-kelas"
        : selectedClassName.toLocaleLowerCase("ms").replaceAll(/\s+/g, "-");
    downloadTextFile(
      `kehadiran-${selectedDate}-${classSlug}.csv`,
      csv,
      "text/csv",
    );
    announce(`${visibleRows.length} rekod dieksport ke fail CSV.`);
  };

  const changeStatus = (
    row: AttendanceRow,
    nextStatus: AttendanceStatus,
  ) => {
    if (row.record) {
      updateAttendance(row.record.id, nextStatus);
      announce(
        `Status ${row.student.name} dikemas kini kepada ${statusLabel[nextStatus]}.`,
      );
      return;
    }

    if (selectedDate !== today) {
      announce("Rekod baharu hanya boleh ditambah untuk tarikh hari ini dalam demo.");
      return;
    }

    const outcome = markAttendance(row.student.code, nextStatus, "admin");
    announce(outcome.message);
  };

  const renderStatusControl = (row: AttendanceRow) => {
    if (!canEditAttendance) {
      return row.record ? (
        <StatusBadge status={row.record.status} />
      ) : (
        <span className="attendance-unrecorded">
          <Clock3 size={13} aria-hidden="true" />
          Belum direkod
        </span>
      );
    }

    const statuses = Array.from(
      new Set<AttendanceStatus>([
        ...settings.attendanceStatuses,
        ...(row.record ? [row.record.status] : []),
      ]),
    );
    const cannotAddHistoricalRecord = !row.record && selectedDate !== today;

    return (
      <select
        className={`attendance-status-select attendance-status-select--${row.status}`}
        value={row.status}
        onChange={(event: ChangeEvent<HTMLSelectElement>) => {
          const nextStatus = event.target.value;
          if (nextStatus !== "unrecorded") {
            changeStatus(row, nextStatus as AttendanceStatus);
          }
        }}
        disabled={cannotAddHistoricalRecord}
        aria-label={`Ubah status ${row.student.name}`}
        title={
          cannotAddHistoricalRecord
            ? "Rekod baharu untuk tarikh lalu tidak tersedia dalam demo"
            : "Ubah status kehadiran"
        }
      >
        {!row.record && (
          <option value="unrecorded" disabled>
            Belum direkod
          </option>
        )}
        {statuses.map((status) => (
          <option key={status} value={status}>
            {statusLabel[status]}
          </option>
        ))}
      </select>
    );
  };

  const statusChoices: AttendanceFilter[] = [
    "all",
    ...settings.attendanceStatuses,
    "unrecorded",
  ];

  const summaryItems = [
    {
      key: "present",
      label: "Hadir",
      value: totals.present,
      icon: Check,
    },
    {
      key: "late",
      label: "Lewat",
      value: totals.late,
      icon: Clock3,
    },
    {
      key: "absent",
      label: "Tidak hadir",
      value: totals.absent,
      icon: X,
    },
    {
      key: "excused",
      label: "Berizin",
      value: totals.excused,
      icon: FileCheck2,
    },
    {
      key: "unrecorded",
      label: "Belum direkod",
      value: totals.unrecorded,
      icon: UserX,
    },
  ] as const;

  return (
    <main className="attendance-page">
      <PageHeader
        eyebrow={session?.role === "teacher" ? "Kelas tugasan" : "Seluruh sekolah"}
        title="Kehadiran"
        description={`${formatDayDate(selectedDate)} · ${selectedClassName}`}
        actions={
          canExport ? (
            <Button
              variant="secondary"
              icon={Download}
              onClick={exportCsv}
              disabled={!visibleRows.length}
            >
              Eksport CSV
            </Button>
          ) : undefined
        }
      />

      {feedback && (
        <div className="attendance-feedback" role="status" aria-live="polite">
          <Check size={17} aria-hidden="true" />
          <span>{feedback}</span>
        </div>
      )}

      <section className="attendance-summary" aria-label="Ringkasan kehadiran">
        {summaryItems.map(({ key, label, value, icon: Icon }) => (
          <button
            key={key}
            type="button"
            className={`attendance-summary__item attendance-summary__item--${key}`}
            aria-pressed={statusFilter === key}
            onClick={() =>
              setStatusFilter(statusFilter === key ? "all" : key)
            }
          >
            <span className="attendance-summary__icon">
              <Icon size={17} aria-hidden="true" />
            </span>
            <span>
              <strong>{value}</strong>
              <small>{label}</small>
            </span>
          </button>
        ))}
      </section>

      <section className="attendance-controls" aria-label="Tapisan kehadiran">
        <div className="form-field attendance-control attendance-control--date">
          <label htmlFor="attendance-date">Tarikh</label>
          <div className="attendance-control__with-icon">
            <CalendarDays size={17} aria-hidden="true" />
            <input
              id="attendance-date"
              type="date"
              value={selectedDate}
              onChange={(event) => event.target.value && setSelectedDate(event.target.value)}
            />
          </div>
        </div>

        <div className="form-field attendance-control attendance-control--class">
          <label htmlFor="attendance-class">Kelas</label>
          {canChooseClass ? (
            <div className="attendance-control__with-icon">
              <Users size={17} aria-hidden="true" />
              <select
                id="attendance-class"
                value={selectedClassId}
                onChange={(event) => setSelectedClassId(event.target.value)}
              >
                <option value="all">Semua kelas</option>
                {availableClasses.map((schoolClass) => (
                  <option key={schoolClass.id} value={schoolClass.id}>
                    {schoolClass.name}
                  </option>
                ))}
              </select>
            </div>
          ) : (
            <div
              className="attendance-fixed-scope"
              id="attendance-class"
              aria-label={`Kelas tugasan: ${selectedClassName}`}
            >
              <Users size={17} aria-hidden="true" />
              <strong>{selectedClassName}</strong>
              <span>Kelas tugasan</span>
            </div>
          )}
        </div>

        <div className="form-field attendance-control attendance-control--status attendance-status-field">
          <label htmlFor="attendance-status">Status</label>
          <select
            id="attendance-status"
            value={statusFilter}
            onChange={(event) =>
              setStatusFilter(event.target.value as AttendanceFilter)
            }
          >
            {statusChoices.map((status) => (
              <option key={status} value={status}>
                {filterLabels[status]}
              </option>
            ))}
          </select>
        </div>

        <div className="attendance-status-chips" aria-label="Tapis mengikut status">
          {statusChoices.map((status) => (
            <button
              key={status}
              type="button"
              aria-pressed={statusFilter === status}
              onClick={() => setStatusFilter(status)}
            >
              {filterLabels[status]}
            </button>
          ))}
        </div>

        <div className="form-field attendance-control attendance-control--search">
          <label htmlFor="attendance-search">Cari murid</label>
          <div className="search-input">
            <Search size={18} aria-hidden="true" />
            <input
              id="attendance-search"
              type="search"
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              placeholder="Nama atau kod murid"
            />
          </div>
        </div>

        <div className="attendance-filter-actions">
          <Button
            type="button"
            variant="tertiary"
            size="compact"
            icon={RotateCcw}
            onClick={resetFilters}
            disabled={!hasActiveFilters}
          >
            Set semula
          </Button>
        </div>
      </section>

      <section className="attendance-work-surface panel" aria-labelledby="attendance-list-title">
        <header className="attendance-work-surface__header">
          <div>
            <h2 id="attendance-list-title">Senarai murid</h2>
            <p>
              Menunjukkan <strong>{visibleRows.length}</strong> daripada{" "}
              <strong>{allRows.length}</strong> murid
            </p>
          </div>
          {canEditAttendance && (
            <span className="attendance-edit-note">
              Status boleh dilaraskan terus
            </span>
          )}
        </header>

        {visibleRows.length ? (
          <>
            <div className="table-wrap attendance-table-wrap desktop-only">
              <table className="data-table attendance-table">
                <thead>
                  <tr>
                    <th scope="col">Murid</th>
                    <th scope="col">Kelas</th>
                    <th scope="col">Status</th>
                    <th scope="col">Masa</th>
                    <th scope="col">Kaedah</th>
                    <th scope="col">Direkod oleh</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleRows.map((row, index) => (
                    <tr key={row.student.id}>
                      <td>
                        <div className="attendance-student-cell">
                          <Avatar
                            initials={row.student.initials}
                            size="sm"
                            tone={index}
                            label={row.student.name}
                          />
                          <div>
                            <strong>{row.student.name}</strong>
                            <span>{row.student.code}</span>
                          </div>
                        </div>
                      </td>
                      <td>{row.student.className}</td>
                      <td>{renderStatusControl(row)}</td>
                      <td className="tabular">
                        {row.record ? formatTime(row.record.timestamp) : "—"}
                      </td>
                      <td>
                        {row.record ? methodLabels[row.record.method] : "—"}
                      </td>
                      <td>
                        <span className="attendance-recorded-by">
                          {row.record?.recordedBy ?? "Belum direkod"}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="attendance-mobile-list mobile-only">
              {visibleRows.map((row, index) => (
                <article className="attendance-mobile-row" key={row.student.id}>
                  <Avatar
                    initials={row.student.initials}
                    size="md"
                    tone={index}
                    label={row.student.name}
                  />
                  <div className="attendance-mobile-row__body">
                    <div className="attendance-mobile-row__heading">
                      <div>
                        <strong>{row.student.name}</strong>
                        <span>
                          {row.student.code} · {row.student.className}
                        </span>
                      </div>
                      {renderStatusControl(row)}
                    </div>
                    <div className="attendance-mobile-row__meta">
                      <span>
                        <Clock3 size={14} aria-hidden="true" />
                        {row.record ? formatTime(row.record.timestamp) : "Tiada masa"}
                      </span>
                      <span>
                        {row.record
                          ? methodLabels[row.record.method]
                          : "Belum direkod"}
                      </span>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </>
        ) : (
          <EmptyState
            icon={Search}
            title={
              allRows.length
                ? "Tiada rekod sepadan"
                : "Tiada murid dalam skop ini"
            }
            message={
              allRows.length
                ? "Cuba kata carian atau status lain. Tarikh dan skop kelas anda dikekalkan."
                : "Pilih kelas lain atau semak profil murid bagi skop yang dipilih."
            }
            action={
              hasActiveFilters ? (
                <Button
                  variant="secondary"
                  icon={RotateCcw}
                  onClick={resetFilters}
                >
                  Kosongkan tapisan
                </Button>
              ) : undefined
            }
          />
        )}
      </section>
    </main>
  );
}


